// Daily-driver shell: sidebar nav + topbar with search/theme/user.
// Used for the Projects / Repos / Artifacts / Build-Info screens
// where DevOps engineers spend their day.

import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  FolderTree,
  Box,
  Activity,
  HelpCircle,
  Lock,
  Shield,
  ShieldAlert,
  ScanLine,
  Key,
  ArrowUpFromDot,
  Bot,
  Trash2,
  ScanSearch,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { WordmarkBase } from './Logo';
import { ToolSwitcher } from './ToolSwitcher';
import { PageContainer } from './PageContainer';
import { ThemeToggle } from './ui/ThemeToggle';
import { SearchBar } from './SearchBar';
import { UserMenu } from './UserMenu';
import { cn } from '../lib/cn';
import { useWhoami } from '../api/client';
import { useMaintenance } from '../api/maintenance';
import { usePublicConfig } from '../api/publicConfig';
import { MaintenanceBanner } from './MaintenanceBanner';
import { SystemBanner } from './SystemBanner';
import { SeedBanner } from './SeedBanner';
import { ImpersonationBanner } from './ImpersonationBanner';

interface NavItem {
  to: string;
  labelKey: string;
  Icon: LucideIcon;
}

const navItems: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard', Icon: LayoutDashboard },
  { to: '/projects', labelKey: 'nav.projects', Icon: FolderTree },
  { to: '/repos', labelKey: 'nav.repositories', Icon: Box },
  { to: '/scans', labelKey: 'nav.scans', Icon: ScanSearch },
  { to: '/builds', labelKey: 'nav.buildInfo', Icon: Activity },
  { to: '/tokens', labelKey: 'nav.apiTokens', Icon: Key },
  { to: '/promotions', labelKey: 'nav.promotions', Icon: ArrowUpFromDot },
  { to: '/service-accounts', labelKey: 'nav.serviceAccounts', Icon: Bot },
  { to: '/trash', labelKey: 'nav.trash', Icon: Trash2 },
];

// Wordmark row. `gap-2.5` MUST live on this wrapper, not only inside
// WordmarkBase — otherwise the slash collides with the suffix.
//
// Before item 255 the wordmark was ONE flex container holding
// glyph · ORBITAL · "/" · REG with `gap-2.5`, and the slash carries
// `-mx-1.5` so it tucks in to a net 4px per side (10 − 6). Phase C
// moved "REG" into ToolSwitcher, which must be a <button> and so
// cannot sit inside the home <a> — it became a sibling of the Link.
// The slash stayed the last child of WordmarkBase, but nothing
// balanced its −6px right margin any more, so the suffix was pulled
// flush against it and the "/" appeared to run into the text.
// Restoring `gap-2.5` here re-creates the intended 4px optical gap.
//
// `font-display` is defensive, not a fix: today it resolves to the
// same Inter as the body face, so it changes nothing visually — but
// it keeps both halves of the wordmark tied to one family should the
// brand face ever diverge. (In orbital-lint the equivalent split DID
// change the face, because its wordmark class carries Outfit.)
//
// Extracted from AppShell so the `gap-2.5` / `-mx-1.5` coupling can be
// regression-tested without pulling in the rest of the shell's hooks
// (see docs/PRODUCT-ROADMAP.md item 259, Phase B2).
export function WordmarkRow() {
  return (
    <div className="flex items-center gap-2.5 font-display px-5 h-16 border-b border-border">
      <Link
        to="/"
        className="flex items-center hover:opacity-80 transition-opacity"
        aria-label="OrbitalReg home"
      >
        <WordmarkBase />
      </Link>
      <ToolSwitcher />
    </div>
  );
}

export function AppShell({ children }: Readonly<{ children: ReactNode }>) {
  const { t } = useTranslation();
  const { user } = useWhoami();
  const { data: maint } = useMaintenance();
  const { data: pubConfig } = usePublicConfig();

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex">
        {/* Sidebar — sticky + self-scrolling so the Governance block stays
            reachable no matter how long the main content (e.g. a big repo
            list) gets. */}
        <aside className="w-60 shrink-0 border-r border-border bg-bg-elevated/40 backdrop-blur-sm flex flex-col sticky top-0 h-screen overflow-y-auto">
          <WordmarkRow />

          <nav className="flex-1 p-3 space-y-0.5">
            <p className="orb-nav-label text-fg-subtle px-3 pt-3 pb-2">{t('nav.workspace')}</p>
            {navItems.map(({ to, labelKey, Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t(labelKey)}</span>
              </NavLink>
            ))}
          </nav>

          {(user?.roles?.includes('org_admin') || user?.roles?.includes('admin')) && (
            <div className="p-3 border-t border-border space-y-0.5">
              <p className="orb-nav-label text-fg-subtle px-3 pb-2">{t('nav.governance')}</p>
              <NavLink
                to="/detection/findings"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <ScanLine className="h-4 w-4 shrink-0" />
                <span>{t('nav.detection')}</span>
              </NavLink>
              <NavLink
                to="/security"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-danger/10 text-danger border border-danger/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>{t('nav.security')}</span>
              </NavLink>
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-nebula/10 text-nebula border border-nebula/30 font-medium'
                      : 'text-fg-muted hover:bg-bg-hover hover:text-fg border border-transparent',
                  )
                }
              >
                <Shield className="h-4 w-4 shrink-0" />
                <span>{t('nav.admin')}</span>
              </NavLink>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="relative z-40 h-16 border-b border-border bg-bg-base/80 backdrop-blur-sm flex items-center justify-between gap-6 px-6 shrink-0">
            <div className="flex-1 min-w-0 flex items-center gap-4">
              <span className="orb-eyebrow text-fg-subtle shrink-0 hidden xl:inline">
                {t('topbar.selfHosted')}
              </span>
              <SearchBar />
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {pubConfig?.air_gapped_mode && (
                <a
                  href="/admin/network"
                  className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md text-xs font-medium bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
                  title={t('topbar.airGappedTitle')}
                >
                  <Lock className="h-3 w-3" />
                  <span>{t('topbar.airGapped')}</span>
                </a>
              )}
              {pubConfig?.help_url && (
                <a
                  href={pubConfig.help_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
                  title={t('topbar.helpTitle')}
                  aria-label={t('topbar.help')}
                >
                  <HelpCircle className="h-4 w-4" />
                </a>
              )}
              <ThemeToggle />
              <UserMenu />
            </div>
          </header>

          <ImpersonationBanner />
          {maint?.active && <MaintenanceBanner reason={maint.reason} until={maint.until} />}
          {pubConfig?.active_banner && <SystemBanner banner={pubConfig.active_banner} />}
          {pubConfig?.seed_active && <SeedBanner setName={pubConfig.seed_set} />}

          <main className="flex-1 py-6">
            <PageContainer>{children}</PageContainer>
          </main>
        </div>
      </div>
    </>
  );
}
